import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Box,
  Alert,
} from '@mui/material';
import axios from '../../config/axiosInstance';

const SCOPE_OPTIONS = [
  { value: 'all', label: 'Todos los usuarios' },
  { value: 'station', label: 'Por estación' },
  { value: 'role', label: 'Por rol' },
  { value: 'users', label: 'Usuarios específicos' },
];

export default function CreateNotificationDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [scopeType, setScopeType] = useState('all');
  const [stationId, setStationId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [userIds, setUserIds] = useState([]);

  const [stations, setStations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setMessage('');
    setScopeType('all');
    setStationId('');
    setRoleId('');
    setUserIds([]);
    setError('');

    axios.get('station').then((res) => setStations(res.data || [])).catch(() => setStations([]));
    axios.get('/roles').then((res) => setRoles(res.data || [])).catch(() => setRoles([]));
    axios.get('/users').then((res) => setUsers(res.data || [])).catch(() => setUsers([]));
  }, [open]);

  const scopeValue = () => {
    if (scopeType === 'station') return stationId;
    if (scopeType === 'role') return roleId;
    if (scopeType === 'users') return userIds;
    return undefined;
  };

  const isScopeIncomplete = () => {
    if (scopeType === 'station') return !stationId;
    if (scopeType === 'role') return !roleId;
    if (scopeType === 'users') return userIds.length === 0;
    return false;
  };

  const handleSend = async () => {
    setError('');
    if (!title.trim() || !message.trim()) {
      setError('Completá el título y el mensaje.');
      return;
    }
    if (isScopeIncomplete()) {
      setError('Elegí el destinatario de la notificación.');
      return;
    }

    setSending(true);
    try {
      await axios.post('/notifications', {
        title: title.trim(),
        message: message.trim(),
        scope: { type: scopeType, value: scopeValue() },
      });
      setSending(false);
      onCreated?.();
      onClose();
    } catch (err) {
      setSending(false);
      setError(err.response?.data?.error || 'No se pudo enviar la notificación.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nueva notificación</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="Título"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <TextField
          label="Mensaje"
          fullWidth
          multiline
          minRows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <FormControl fullWidth>
          <InputLabel>Enviar a</InputLabel>
          <Select
            value={scopeType}
            label="Enviar a"
            onChange={(e) => setScopeType(e.target.value)}
          >
            {SCOPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {scopeType === 'station' && (
          <FormControl fullWidth>
            <InputLabel>Estación</InputLabel>
            <Select value={stationId} label="Estación" onChange={(e) => setStationId(e.target.value)}>
              {stations.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {scopeType === 'role' && (
          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select value={roleId} label="Rol" onChange={(e) => setRoleId(e.target.value)}>
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.name} – {r.description}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {scopeType === 'users' && (
          <FormControl fullWidth>
            <InputLabel>Usuarios</InputLabel>
            <Select
              multiple
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
              input={<OutlinedInput label="Usuarios" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const u = users.find((usr) => usr.id === id);
                    return <Chip key={id} label={u?.name || id} size="small" />;
                  })}
                </Box>
              )}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancelar</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending}>
          {sending ? 'Enviando…' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
